import { searchable as folded } from "@/lib/search";

/** A match in a text: from `start` up to, not including, `end` (string indices). */
export type HighlightRange = readonly [start: number, end: number];

/**
 * The text folded for comparison, with where each folded character came from
 * in the original, so a match can be shown in the original's own casing and
 * accents. A combining accent folds to nothing and joins the letter before it.
 */
function foldWithOrigins(text: string): { text: string; starts: number[]; ends: number[] } {
  let out = "";
  const starts: number[] = [];
  const ends: number[] = [];
  let index = 0;
  for (const char of text) {
    const end = index + char.length;
    const fold = folded(char);
    if (fold === "" && ends.length > 0) {
      ends[ends.length - 1] = end;
    }
    for (let k = 0; k < fold.length; k += 1) {
      starts.push(index);
      ends.push(end);
    }
    out += fold;
    index = end;
  }
  return { text: out, starts, ends };
}

/**
 * Where the words of `query` occur in `text`, ignoring case and accents: every
 * occurrence of every word, overlapping ones merged, in order, as indices into
 * the original text. An empty query matches nothing.
 */
export function highlightRanges(text: string, query: string): HighlightRange[] {
  const words = folded(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return [];
  }
  const source = foldWithOrigins(text);
  const found: [number, number][] = [];
  for (const word of words) {
    for (let at = source.text.indexOf(word); at !== -1; at = source.text.indexOf(word, at + 1)) {
      const start = source.starts[at];
      const end = source.ends[at + word.length - 1];
      if (start !== undefined && end !== undefined) {
        found.push([start, end]);
      }
    }
  }
  found.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const merged: [number, number][] = [];
  for (const [start, end] of found) {
    const last = merged.at(-1);
    if (last && start <= last[1]) {
      last[1] = Math.max(last[1], end);
    } else {
      merged.push([start, end]);
    }
  }
  return merged;
}
